import React from 'react';
import { Mission, MissionStatus, MissionType } from '../../types';
import { formatMissionStatus } from '../../utils/formatUtils';
import { format } from 'date-fns';

interface StatusStepperProps {
  mission: Mission;
}

// IFT missions get different labels for the same phases
const IFT_LABELS: Partial<Record<string, string>> = {
  RECEIVED: 'Transfer Requested',
  ON_SCENE: 'At Sending Facility',
  AT_HOSPITAL: 'At Receiving Facility',
};

const PHASE_ORDER = [
  'RECEIVED',
  'DISPATCHED',
  'AIRBORNE',
  'ON_SCENE',
  'TRANSPORTING',
  'AT_HOSPITAL',
  'COMPLETE',
] as const;

type Phase = typeof PHASE_ORDER[number];

interface PhaseConfig {
  phase: Phase;
  label: string;
  getTimestamp: (mission: Mission) => Date | undefined;
}

const PHASES: PhaseConfig[] = [
  {
    phase: 'RECEIVED',
    label: 'Call Received',
    getTimestamp: (m) => m.timeReceived ? new Date(m.timeReceived) : undefined,
  },
  {
    phase: 'DISPATCHED',
    label: 'Dispatched',
    getTimestamp: (m) => {
      const t = m.timeAircraftNotified ?? m.timeAssigned;
      return t ? new Date(t) : undefined;
    },
  },
  {
    phase: 'AIRBORNE',
    label: 'Airborne',
    getTimestamp: (m) => m.timeAircraftLaunched ? new Date(m.timeAircraftLaunched) : undefined,
  },
  {
    phase: 'ON_SCENE',
    label: 'On Scene',
    getTimestamp: (m) => m.timeOnScene ? new Date(m.timeOnScene) : undefined,
  },
  {
    phase: 'TRANSPORTING',
    label: 'Transporting',
    getTimestamp: (m) => m.timeTransporting ? new Date(m.timeTransporting) : undefined,
  },
  {
    phase: 'AT_HOSPITAL',
    label: 'At Hospital',
    getTimestamp: (m) => m.timeAtDestination ? new Date(m.timeAtDestination) : undefined,
  },
  {
    phase: 'COMPLETE',
    label: 'Complete',
    getTimestamp: (m) => m.timeMissionComplete ? new Date(m.timeMissionComplete) : undefined,
  },
];

const STATUS_TO_PHASE: Record<string, Phase> = {
  [MissionStatus.RECEIVED]: 'RECEIVED',
  [MissionStatus.ASSIGNED]: 'DISPATCHED',
  [MissionStatus.DISPATCHED]: 'DISPATCHED',
  [MissionStatus.CREW_NOTIFIED]: 'DISPATCHED',
  [MissionStatus.AIRCRAFT_NOTIFIED]: 'DISPATCHED',
  [MissionStatus.AIRCRAFT_LAUNCHED]: 'AIRBORNE',
  [MissionStatus.AIRCRAFT_AT_CREW_BASE]: 'AIRBORNE',
  [MissionStatus.CREW_ABOARD]: 'AIRBORNE',
  [MissionStatus.ENROUTE_TO_SCENE]: 'AIRBORNE',
  [MissionStatus.ON_SCENE]: 'ON_SCENE',
  [MissionStatus.PATIENT_CONTACT]: 'ON_SCENE',
  [MissionStatus.TRANSPORTING]: 'TRANSPORTING',
  [MissionStatus.AT_DESTINATION]: 'AT_HOSPITAL',
  [MissionStatus.MISSION_COMPLETE]: 'COMPLETE',
};

function getPhaseForStatus(status: string): Phase {
  return STATUS_TO_PHASE[status] || 'RECEIVED';
}

function getPhaseIndex(phase: Phase): number {
  return PHASE_ORDER.indexOf(phase);
}

function getStepState(
  phaseIndex: number,
  currentPhaseIndex: number
): 'completed' | 'current' | 'future' {
  if (phaseIndex < currentPhaseIndex) return 'completed';
  if (phaseIndex === currentPhaseIndex) return 'current';
  return 'future';
}

const StatusStepper: React.FC<StatusStepperProps> = ({ mission }) => {
  const isCancelled = mission.status === MissionStatus.CANCELLED;
  const isClosed = mission.status === MissionStatus.CLOSED;
  const isTerminal = isCancelled || isClosed;

  const currentPhase = getPhaseForStatus(mission.status);
  const currentPhaseIndex = getPhaseIndex(currentPhase);
  const isIFT = mission.missionType === MissionType.INTER_FACILITY;

  const getLabel = (phaseConfig: PhaseConfig) => {
    if (isIFT && IFT_LABELS[phaseConfig.phase]) return IFT_LABELS[phaseConfig.phase]!;
    return phaseConfig.label;
  };

  return (
    <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
      {/* Terminal state banner */}
      {isTerminal && (
        <div className={`mb-3 px-3 py-2 rounded-md text-sm font-medium text-center ${
          isCancelled
            ? 'bg-red-50 text-red-700 border border-red-200'
            : 'bg-gray-100 text-gray-600 border border-gray-300'
        }`}>
          {isCancelled ? 'Mission Cancelled' : 'Mission Closed'}
          {mission.dateClosed && (
            <span className="ml-2 font-normal">
              — {format(new Date(mission.dateClosed), 'HH:mm dd/MM/yyyy')}
            </span>
          )}
        </div>
      )}

      {/* Desktop stepper */}
      <div className="hidden md:block">
        <div className="flex items-start justify-between">
          {PHASES.map((phaseConfig, index) => {
            const timestamp = phaseConfig.getTimestamp(mission);
            const state = isTerminal
              ? (timestamp ? 'completed' : 'future')
              : getStepState(index, currentPhaseIndex);
            const isLast = index === PHASES.length - 1;

            return (
              <React.Fragment key={phaseConfig.phase}>
                <div className="flex flex-col items-center flex-shrink-0" style={{ minWidth: '4rem' }}>
                  {/* Circle */}
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                      state === 'current'
                        ? 'bg-blue-900 text-white border-blue-900'
                        : state === 'completed'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-gray-200 text-gray-500 border-gray-300'
                    }`}
                  >
                    {state === 'completed' ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </div>

                  {/* Label */}
                  <span className={`mt-1.5 text-xs text-center leading-tight ${
                    state === 'current'
                      ? 'font-semibold text-blue-900'
                      : state === 'completed'
                      ? 'font-medium text-blue-600'
                      : 'text-gray-500'
                  }`}>
                    {getLabel(phaseConfig)}
                  </span>

                  {/* Timestamp */}
                  {timestamp && (
                    <span className="mt-0.5 text-[10px] text-gray-400">
                      {format(timestamp, 'HH:mm')}
                    </span>
                  )}
                </div>

                {/* Connecting line */}
                {!isLast && (
                  <div className="flex-1 flex items-center pt-4 px-1">
                    <div className={`h-0.5 w-full ${
                      state === 'completed'
                        ? 'bg-blue-600'
                        : 'bg-gray-200'
                    }`} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Mobile compact stepper */}
      <div className="md:hidden">
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
          {PHASES.map((phaseConfig, index) => {
            const timestamp = phaseConfig.getTimestamp(mission);
            const state = isTerminal
              ? (timestamp ? 'completed' : 'future')
              : getStepState(index, currentPhaseIndex);
            const isLast = index === PHASES.length - 1;

            return (
              <React.Fragment key={phaseConfig.phase}>
                <div
                  className={`w-3 h-3 rounded-full flex-shrink-0 ${
                    state === 'current'
                      ? 'bg-blue-900 ring-2 ring-blue-300'
                      : state === 'completed'
                      ? 'bg-blue-600'
                      : 'bg-gray-200'
                  }`}
                  title={`${getLabel(phaseConfig)}${timestamp ? ` — ${format(timestamp, 'HH:mm')}` : ''}`}
                />
                {!isLast && (
                  <div className={`flex-1 h-0.5 min-w-[8px] ${
                    state === 'completed' ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        {/* Current status label on mobile */}
        <p className="mt-1.5 text-xs font-medium text-blue-900 text-center">
          {formatMissionStatus(mission.status)}
        </p>
      </div>
    </div>
  );
};

export default StatusStepper;
